// V2-TW embed config + token acquisition. Fourth instance of the V2-GAC embed pattern (see utils/v2gac.ts,
// which documents the security model in detail; utils/v2events.ts and utils/v2tb.ts are the second and third).
//
// PRODUCTION PATH (default): the token is minted SERVER-SIDE by HotService (`POST tw/v2/token`, added to the
// existing TWController) — the server derives playerId AND the GOH guildId from the authenticated session and
// stamps aud="tw", which the TW BFF requires. The signing secret never reaches the browser.
//
// DEV FALLBACK (opt-in only): signDevTWToken() mints the HS256 token IN THE BROWSER with a shared secret —
// forgeable, never in production. Enable explicitly with REACT_APP_V2TW_DEV_SIGN=true.

import BaseAPI from "../service/BaseAPI";

// The embedded HotUtilsV2-TW app's origin, per environment. Override with REACT_APP_V2TW_URL.
export function getV2TWUrl(): string {
  const override = process.env.REACT_APP_V2TW_URL;
  if (override) return override;

  const host = window.location.host;
  // ⚠️ PROD + STAGING point at the container app's own FQDN, not <app>.hotutils.com. Those subdomains
  // were an aspirational convention copied from the first GAC embed and were NEVER provisioned - all twelve
  // failed to resolve (verified 2026-08-12). There is no staging container app either, so staging uses the
  // PROD one, which already accepts staging.hotutils.com as a framer via its PARENT_ORIGIN allowlist.
  // 🔴 The tab is still gated OFF outside development by isV2TWEnabled() - fixing the URL first means the
  // wiring is correct with nothing user-visible to roll back.
  if (host === 'hotutils.com' || host === 'www.hotutils.com') return 'https://hotutils-tw-prod.thankfulocean-ff8d284e.southcentralus.azurecontainerapps.io';
  if (host.includes('localhost')) return 'http://localhost:5176'; // local TW dev server (GAC 5173, Events 5174, TB 5175)
  // Deployed development site -> the deployed dev TW container app.
  if (host.startsWith('development')) return 'https://hotutils-tw-dev.greenbush-0af98d9e.southcentralus.azurecontainerapps.io';
  return 'https://hotutils-tw-prod.thankfulocean-ff8d284e.southcentralus.azurecontainerapps.io';
}

export function getV2TWOrigin(): string {
  return new URL(getV2TWUrl()).origin;
}

/**
 * The V2-TW tab is DEV-ONLY for now — only surfaced (tab + route) on the Development site and in local dev;
 * suppressed on staging/production until ready. Same gating rule as V2-GAC, V2-Events and V2-TB.
 *
 * The ROUTE and the MENU ITEM must be gated by this same function, or the tab appears where it cannot work.
 */
export function isV2TWEnabled(): boolean {
  const host = window.location.host;
  return host === 'development.hotutils.com' || host.startsWith('localhost');
}

/** True when the dev-only in-browser signer should be used instead of the server endpoint (local dev only). */
export function useTWDevSigner(): boolean {
  return process.env.REACT_APP_V2TW_DEV_SIGN === 'true';
}

interface TWV2TokenResponse {
  token?: string;
  issuedAtUnix?: number;
  expiresAtUnix?: number;
}

/**
 * PRODUCTION PATH: ask HotService to mint the embed token (`POST tw/v2/token` on TWController). The server
 * derives playerId + the GOH guildId from the session and signs with aud="tw"; the browser only receives the
 * finished JWT.
 */
export async function fetchTWToken(sessionId?: string): Promise<string> {
  const resp: TWV2TokenResponse = await BaseAPI.postToApi('tw/v2/token', { sessionId: sessionId ?? null });
  if (!resp || !resp.token) throw new Error('TW token endpoint returned no token');
  return resp.token;
}

function base64Url(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlStr(s: string): string {
  return base64Url(new TextEncoder().encode(s));
}

export interface V2TWTokenClaims {
  sub: string;
  sessionId?: string;
  allyCode?: string;
  playerId?: string;
  guildId?: string;
}

// The shared HS256 secret the TW BFF verifies with (PARENT_TOKEN_SECRET). Dev default matches the TW app's
// .env.example. Override with REACT_APP_V2TW_DEV_SECRET. Dev fallback signer only.
function getDevSecret(): string {
  return process.env.REACT_APP_V2TW_DEV_SECRET || 'dev-shared-secret-change-me';
}

// The GOH guild id (string) for the DEV SIGNER ONLY. The website client only has the legacy numeric guild id,
// so a dev-signed token's guild scope must come from env config — deliberately NO default: the production
// path (HotService) derives the real guild server-side, so this matters only when REACT_APP_V2TW_DEV_SIGN=true.
function getDevGuildId(): string | undefined {
  return process.env.REACT_APP_V2TW_DEV_GUILD_ID || undefined;
}

/**
 * DEV-ONLY FALLBACK: mint an HS256 JWT the TW BFF will verify. aud MUST be "tw" — the TW BFF is configured
 * with PARENT_TOKEN_AUDIENCE=tw, so another app's token can never authorize the TW app and vice versa.
 */
export async function signDevTWToken(claims: V2TWTokenClaims): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const devGuildId = getDevGuildId();
  const payload = {
    ...(devGuildId ? { guildId: devGuildId } : {}),
    ...claims,
    iss: 'hotutils',
    aud: 'tw',
    iat: now,
    exp: now + 60 * 60, // 1h
  };
  const header = { alg: 'HS256', typ: 'JWT' };
  const data = `${base64UrlStr(JSON.stringify(header))}.${base64UrlStr(JSON.stringify(payload))}`;

  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(getDevSecret()),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(data));
  return `${data}.${base64Url(new Uint8Array(sig))}`;
}
